/**
 * Copyright (c) Mayoral Moda Infantil, S.A.U. and affiliates.
 */

import {
  alpha,
  Link as MuiLink,
  LinkProps as MuiLinkProps,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { FC, useMemo } from 'react';

const SidebarMuiLink = styled(MuiLink, {
  name: 'SidebarLink',
  slot: 'MuiLink',
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ theme, active }) => ({
  position: 'relative',
  padding: theme.spacing(1, 1.3),
  color: theme.palette.dark[600],
  textDecoration: 'none',
  borderRadius: 2,
  cursor: 'pointer',
  transition: theme.transitions.create('all', {
    duration: theme.transitions.duration.short,
    easing: theme.transitions.easing.easeInOut,
  }),
  '&:hover': {
    color: theme.palette.dark[800],
    backgroundColor: theme.palette.grey[100],
  },
  '&:active': {
    color: theme.palette.dark[800],
    backgroundColor: theme.palette.grey[200],
  },
  ...(active && {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '50%',
      right: theme.spacing(-2),
      width: theme.spacing(0.5),
      height: theme.spacing(3.375),
      backgroundColor: theme.palette.primary.main,
      borderTopLeftRadius: 4,
      borderBottomLeftRadius: 4,
      transform: 'translateY(-50%)',
      pointerEvents: 'none',
    },
  }),
}));

type SidebarLinkProps = {
  label: string;
  href?: string;
  onClick?: () => void;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  LinkProps?: MuiLinkProps;
};

/**
 * Sidebar Link
 */
const SidebarLink: FC<SidebarLinkProps> = ({
  href,
  label,
  onClick,
  startAdornment,
  endAdornment,
  LinkProps,
}) => {
  const router = useRouter();

  // Memoized link status based on the current path
  const active = useMemo(() => {
    if (href !== '/') {
      return router.asPath.startsWith(href);
    }
    return router.pathname === href;
  }, [router.pathname]);

  const LinkComponent = (
    <SidebarMuiLink active={active} onClick={onClick} {...LinkProps}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1.2} sx={{ width: '100%', overflow: 'hidden' }}>
          {startAdornment && (
            <Stack alignItems="center" justifyContent="center">
              {startAdornment}
            </Stack>
          )}
          <Typography component="span" variant="body1" sx={{ fontSize: 14 }} noWrap>
            {label}
          </Typography>
        </Stack>

        {endAdornment}
      </Stack>
    </SidebarMuiLink>
  );

  if (href) {
    return (
      <Link href={href} passHref>
        {LinkComponent}
      </Link>
    );
  }

  return LinkComponent;
};

export default SidebarLink;
